import React, { useEffect, useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import { desktop } from 'styles/config/_breakpoints.scss'

import { Container } from 'components/layout'
import useUIContext from 'context/ui'

import WithScrollbar from './WithScrollbar'
import Tracks from './Tracks'

const HorizontalScroll = ({ children, triggerRequest = true, minimal, ...props }) => {
  const [hasRequested, setHasRequested] = useState(false)
  const [shouldRequest, setShouldRequest] = useState(false)
  const setScrollDirection = useUIContext((s) => s.setScrollDirection)
  const scrollX = useUIContext((s) => s.scrollX)
  const isHorizontalScrollActive = useUIContext((s) => s.isHorizontalScrollActive)
  const setIsHorizontalScrollActive = useUIContext((s) => s.setIsHorizontalScrollActive)
  const requestHorizontalScroll = useUIContext((s) => s.requestHorizontalScroll)
  const releaseHorizontalScroll = useUIContext((s) => s.releaseHorizontalScroll)
  const currentLayout = useUIContext((s) => s.currentLayout)
  const scrollProgress = useUIContext((s) => s.scrollProgress)
  // figure out if children are a function or not
  const childFunc = useMemo(() => (typeof children === 'function' ? children : () => children), [children])

  useEffect(() => {
    // it might sound redundant
    // but it makes sure we don’t call request or release more than once for each component
    if (!triggerRequest) return
    if (shouldRequest && !hasRequested) {
      setHasRequested(true)
      requestHorizontalScroll()
    }
    if (!shouldRequest && hasRequested) {
      setHasRequested(false)
      releaseHorizontalScroll()
    }
    return () => {
      if (!hasRequested) return
      setHasRequested(false)
      releaseHorizontalScroll()
    }
  }, [shouldRequest, requestHorizontalScroll, releaseHorizontalScroll, triggerRequest, hasRequested])

  // request scrollbar use when mounting and release scrollbar use when unmounting
  useEffect(() => {
    if (!triggerRequest) return
    setShouldRequest(window.innerWidth >= parseInt(desktop))
  }, [setShouldRequest, releaseHorizontalScroll, hasRequested, currentLayout, triggerRequest])

  // attach listener to see if changed
  useEffect(() => {
    const onResize = (e) => {
      console.log(e.target.innerWidth)
      const isDesktop = e.target.innerWidth >= parseInt(desktop)
      setShouldRequest(isDesktop)
      setIsHorizontalScrollActive(isDesktop)
    }

    if (triggerRequest) {
      window.addEventListener('resize', onResize)
    }
    return () => {
      if (triggerRequest) {
        window.removeEventListener('resize', onResize)
      }
    }
  }, [setShouldRequest, triggerRequest])

  const onScroll = useCallback((e) => {
    const { x } = e.offset
    setScrollDirection(x > scrollX.current ? -1 : 1)
    scrollProgress.set(x / e.limit.x)
    scrollX.set(x)
  }, [])

  const onInit = useCallback(() => {
    scrollX.set(0)
    setIsHorizontalScrollActive(true)
  }, [])

  // const onDestroy = () => ({})

  useEffect(() => {
    if (!triggerRequest) return
    console.log('currentLayout: HorizontalScroll mounted')
    return () => {
      console.log('currentLayout: HorizontalScroll unmounted')
    }
  }, [triggerRequest])

  return (
    <>
      {currentLayout === 'horizontal' && (
        <WithScrollbar onScroll={onScroll} onInit={onInit} {...props}>
          {childFunc}
        </WithScrollbar>
      )}
      {currentLayout !== 'horizontal' && <Container>{childFunc({})}</Container>}
      {isHorizontalScrollActive && <Tracks minimal={minimal} />}
    </>
  )
}

HorizontalScroll.propTypes = {
  minimal: PropTypes.bool,
  triggerRequest: PropTypes.bool,
}

// HorizontalScroll.whyDidYouRender = true

export default HorizontalScroll
